import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import {
  type Condition,
  CONDITIONS,
  createConditionName,
} from 'types/notification';
import { type EditStep, EDIT_STEP } from 'page/NotificationPage/types/editStep';
import { NotificationFormFields } from 'page/NotificationPage/types/notificationFormField';

/**
 * お知らせ編集・削除画面の状態
 */
interface EditState {
  /**
   * 編集・削除モーダル表示フラグ
   */
  isOpen: boolean;
  /**
   * 編集・削除のステップ
   */
  step: EditStep;
  /**
   * タイトル
   */
  title: string;
  /**
   * 本文
   */
  message: string;
  /**
   * お知らせ種別（重要なら true）
   */
  isHigh: boolean;
  /**
   * 開始日
   */
  startDate: string;
  /**
   * 終了日
   */
  endDate: string;
  /**
   * 配信対象顧客
   */
  condition: Condition;
}

/**
 * お知らせ編集・削除画面の初期状態
 */
const initialState: EditState = {
  isOpen: false,
  step: EDIT_STEP.INPUT,
  title: '',
  message: '',
  isHigh: false,
  startDate: '',
  endDate: '',
  condition: CONDITIONS.ALL,
};

/**
 * お知らせ編集・削除画面の slice(reducer + action)
 */
export const editSlice = createSlice({
  name: 'edit',
  initialState,
  reducers: {
    init: () => initialState,
    modalOpened: (state) => {
      state.isOpen = true;
    },
    stepMovedToConfirmation: (state) => {
      state.step = EDIT_STEP.CONFIRMATION;
    },
    stepMovedToInput: (state) => {
      state.step = EDIT_STEP.INPUT;
    },
    stepMovedToDelete: (state) => {
      state.step = EDIT_STEP.DELETE;
    },
    conditionUpdated: (
      state,
      action: PayloadAction<{ condition: Condition }>,
    ) => {
      state.condition = action.payload.condition;
    },
    formGroupUpdated: (
      state,
      action: PayloadAction<{
        form: NotificationFormFields;
      }>,
    ) => {
      state.title = action.payload.form.title;
      state.message = action.payload.form.message;
      state.isHigh = action.payload.form.isHigh;
      state.startDate = action.payload.form.startDate;
      state.endDate = action.payload.form.endDate;
      state.condition = action.payload.form.condition;
    },
  },
});

/**
 * お知らせ編集・画面の action
 */
export const editActions = editSlice.actions;

/**
 * お知らせ編集・削除画面の selectors
 */
export const editSelectors = {
  isOpenInputModal: (state: RootState) =>
    state.notification.edit.isOpen &&
    state.notification.edit.step === EDIT_STEP.INPUT,
  isOpenConfirmationModal: (state: RootState) =>
    state.notification.edit.isOpen &&
    state.notification.edit.step === EDIT_STEP.CONFIRMATION,
  isOpenDeleteModal: (state: RootState) =>
    state.notification.edit.isOpen &&
    state.notification.edit.step === EDIT_STEP.DELETE,
  title: (state: RootState) => state.notification.edit.title,
  message: (state: RootState) => state.notification.edit.message,
  isHigh: (state: RootState) => state.notification.edit.isHigh,
  startDate: (state: RootState) => state.notification.edit.startDate,
  endDate: (state: RootState) => state.notification.edit.endDate,
  condition: (state: RootState) => state.notification.edit.condition,
  // 表示用の配信対象顧客
  conditionForDisplay: (state: RootState) => {
    const { condition } = state.notification.edit;

    return createConditionName(condition);
  },
  // 顧客一覧表示フラグ
  shouldShowCustomerList: (state: RootState) =>
    state.notification.edit.condition !== CONDITIONS.ALL,
} as const;
